import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap, faSearch, faTimes, faCertificate, faFilter,
  faLayerGroup, faCrown, faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import CategorizedExamList from '../components/CategorizedExamList';
import SEO from '../components/SEO';
import LocalizedLink from '../components/LocalizedLink';

interface Exam {
  id: number;
  name: string;
  description: string;
  is_free_available: boolean;
  category?: string;
  question_count?: number;
  badge_image?: string;
}

interface Subcategory {
  subcategory: string | null;
  exams: Exam[];
}

interface Category {
  category: string;
  subcategories: Subcategory[];
}

const Certifications: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedCategory = searchParams.get('category');
  const qParam = searchParams.get('q');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) setSearchQuery(selectedCategory);
    else if (qParam) setSearchQuery(qParam);
  }, [selectedCategory, qParam]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/exams/categories');
      setCategories(response.data.categories || []);
    } catch {
      try {
        const examsRes = await api.get('/exams');
        const exams: Exam[] = examsRes.data.exams || [];
        const grouped = exams.reduce((acc, exam) => {
          const cat = exam.category || 'Other';
          if (!acc[cat]) acc[cat] = [];
          acc[cat].push(exam);
          return acc;
        }, {} as Record<string, Exam[]>);
        setCategories(
          Object.entries(grouped).map(([category, catExams]) => ({
            category,
            subcategories: [{ subcategory: null, exams: catExams }],
          })).sort((a, b) => a.category.localeCompare(b.category))
        );
      } catch {
        /* empty */
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories
    .map((cat) => {
      if (selectedCategory && cat.category !== selectedCategory) {
        return { ...cat, subcategories: [] };
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return {
          ...cat,
          subcategories: cat.subcategories
            .map((sub) => ({
              ...sub,
              exams: sub.exams.filter(
                (exam) =>
                  exam.name.toLowerCase().includes(query) ||
                  exam.description?.toLowerCase().includes(query) ||
                  cat.category.toLowerCase().includes(query) ||
                  (sub.subcategory && sub.subcategory.toLowerCase().includes(query))
              ),
            }))
            .filter((sub) => sub.exams.length > 0),
        };
      }
      return cat;
    })
    .filter((cat) => cat.subcategories.some((sub) => sub.exams.length > 0));

  const clearCategoryFilter = () => {
    setSearchParams({});
    setSearchQuery('');
  };

  const totalExams = categories.reduce(
    (sum, cat) => sum + cat.subcategories.reduce((subSum, sub) => subSum + sub.exams.length, 0),
    0
  );

  const filteredCount = filteredCategories.reduce(
    (sum, cat) => sum + cat.subcategories.reduce((subSum, sub) => subSum + sub.exams.length, 0),
    0
  );

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div
          className="w-12 h-12 rounded-full border-2 animate-spin"
          style={{ borderTopColor: 'var(--gp-brand)', borderRightColor: 'var(--gp-brand)' }}
          role="status"
          aria-label={t('common.loading')}
        />
      </div>
    );
  }

  return (
    <>
      <SEO
        title={t('certifications.seo.title')}
        description={t('certifications.seo.description', { count: totalExams })}
        keywords={t('certifications.seo.keywords')}
        url="/certifications"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: t('certifications.title'),
          numberOfItems: totalExams,
          itemListElement: categories.flatMap((category, catIndex) =>
            category.subcategories.flatMap((subcategory, subIndex) =>
              subcategory.exams.map((exam, examIndex) => ({
                '@type': 'ListItem',
                position: catIndex * 1000 + subIndex * 100 + examIndex + 1,
                item: {
                  '@type': 'Course',
                  name: exam.name,
                  description: exam.description,
                  provider: { '@type': 'Organization', name: 'GlobalPreps' },
                },
              }))
            )
          ),
        }}
        breadcrumbs={[
          { name: t('nav.home'), url: '/' },
          { name: t('nav.certifications'), url: '/certifications' },
        ]}
      />

      <div className="gp-page">
        <div className="gp-container pt-6 sm:pt-8 pb-10 sm:pb-12">
          <header className="mb-5">
            <span className="gp-badge gp-badge-brand mb-2">
              <FontAwesomeIcon icon={faCertificate} aria-hidden="true" />
              {t('certifications.badge', { count: totalExams })}
            </span>
            <h1 className="gp-heading-xl mb-2">{t('certifications.title')}</h1>
            <p className="text-sm sm:text-base gp-text-muted max-w-2xl">{t('certifications.description')}</p>
          </header>

          <div className="gp-promo-banner">
            <div className="flex items-start gap-2.5">
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                style={{ background: 'var(--gp-brand)', color: '#fff' }}
              >
                <FontAwesomeIcon icon={faCrown} className="text-sm" aria-hidden="true" />
              </div>
              <div>
                <h2 className="font-semibold text-sm mb-0.5" style={{ color: 'var(--gp-ink)' }}>
                  {t('certifications.promoTitle')}
                </h2>
                <p className="text-xs gp-text-muted">{t('certifications.promoDescription')}</p>
              </div>
            </div>
            <LocalizedLink to="/pricing" className="gp-btn-accent shrink-0 text-sm">
              {t('certifications.viewPricing')}
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </LocalizedLink>
          </div>

          <div className="mb-5">
            <div className="flex flex-col sm:flex-row gap-2.5">
              <div className="flex-1 relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none"
                  style={{ color: 'var(--gp-ink-subtle)' }}
                  aria-hidden="true"
                />
                <input
                  type="search"
                  placeholder={t('certifications.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (selectedCategory && e.target.value !== selectedCategory) {
                      setSearchParams({});
                    }
                  }}
                  className="gp-search-input"
                  aria-label={t('certifications.searchPlaceholder')}
                />
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faFilter} className="text-xs" style={{ color: 'var(--gp-ink-subtle)' }} aria-hidden="true" />
                <select
                  value={selectedCategory || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setSearchParams({ category: e.target.value });
                      setSearchQuery(e.target.value);
                    } else {
                      clearCategoryFilter();
                    }
                  }}
                  className="gp-select"
                  aria-label={t('certifications.filterByCategory')}
                >
                  <option value="">{t('certifications.filterByCategory')}</option>
                  {categories.map((cat) => {
                    const examCount = cat.subcategories.reduce((sum, sub) => sum + sub.exams.length, 0);
                    return (
                      <option key={cat.category} value={cat.category}>
                        {cat.category} ({examCount})
                      </option>
                    );
                  })}
                </select>
                {selectedCategory && (
                  <button
                    type="button"
                    onClick={clearCategoryFilter}
                    className="p-2 rounded-md transition-colors"
                    style={{ color: 'var(--gp-ink-muted)', background: 'var(--gp-surface-muted)' }}
                    title={t('certifications.clearSearch')}
                    aria-label={t('certifications.clearSearch')}
                  >
                    <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-2.5 text-xs gp-text-muted">
              <span className="inline-flex items-center gap-1.5">
                <FontAwesomeIcon icon={faLayerGroup} style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                {categories.length} {categories.length === 1 ? 'category' : 'categories'}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <FontAwesomeIcon icon={faGraduationCap} style={{ color: 'var(--gp-success)' }} aria-hidden="true" />
                {searchQuery || selectedCategory
                  ? t('certifications.showingCount', { count: filteredCount, total: totalExams })
                  : `${totalExams} exams`}
              </span>
            </div>
          </div>

          {filteredCategories.length === 0 ? (
            <div className="gp-card text-center py-10">
              <div
                className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center"
                style={{ background: 'var(--gp-surface-muted)' }}
              >
                <FontAwesomeIcon icon={faSearch} className="text-lg" style={{ color: 'var(--gp-ink-subtle)' }} aria-hidden="true" />
              </div>
              <h3 className="font-display text-base font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                {searchQuery ? t('certifications.noResults') : t('certifications.noCertifications')}
              </h3>
              {searchQuery && (
                <button type="button" onClick={clearCategoryFilter} className="gp-btn-primary mt-4 inline-flex gap-2">
                  {t('certifications.clearSearch')}
                  <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                </button>
              )}
            </div>
          ) : (
            <CategorizedExamList categories={filteredCategories} />
          )}
        </div>
      </div>
    </>
  );
};

export default Certifications;
